import { type components, unwrap } from '@shared/api-client/index';
import { toastApiError } from '@shared/ui/toast/toast-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../core/api';

export type Member = components['schemas']['MemberResponse'];
export type MembershipRole = Member['role'];

export const membersKey = ['members'] as const;
/** The seat roster query (active + disabled), a sub-key so optimistic writes can target it directly. */
export const seatMembersKey = [...membersKey, 'seats'] as const;

/**
 * Billing-status query key, inlined here rather than imported from billing.hooks:
 * billing.hooks already imports this module, so importing it back would form a
 * members <-> billing cycle. Kept in sync with `billingStatusKey` in billing.hooks.
 */
const billingStatusKey = ['billing-status'] as const;

/**
 * All seat-holding members (active and disabled) for the admin seat controls and
 * the members-management page (SHELL-4). Keyed under the `members` prefix so seat
 * and role/remove mutations invalidating `['members']` refetch this too, keeping
 * the billing seat count and the member list on one source of truth.
 */
export function useSeatMembers() {
	return useQuery({
		queryKey: seatMembersKey,
		queryFn: async () =>
			unwrap(await api.GET('/v1/members', { params: { query: { includeDisabled: true } } })),
	});
}

/**
 * Promote or demote a member. Admin-only; the api refuses to demote the last
 * admin. Optimistically flips the role in the seat cache so the Select responds
 * instantly, rolling back on error and reconciling from the server on settle.
 */
export function useChangeRole() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ userId, role }: { userId: string; role: MembershipRole }) =>
			unwrap(
				await api.PATCH('/v1/members/{userId}/role', {
					params: { path: { userId } },
					body: { role },
				}),
			),
		onMutate: async ({ userId, role }) => {
			await queryClient.cancelQueries({ queryKey: seatMembersKey });
			const previous = queryClient.getQueryData<Member[]>(seatMembersKey);
			queryClient.setQueryData<Member[]>(seatMembersKey, (members) =>
				members?.map((member) => (member.userId === userId ? { ...member, role } : member)),
			);
			return { previous };
		},
		onError: (error, _variables, context) => {
			if (context?.previous) {
				queryClient.setQueryData(seatMembersKey, context.previous);
			}
			toastApiError(error);
		},
		onSettled: () => queryClient.invalidateQueries({ queryKey: membersKey }),
	});
}

/** Remove a member from the workspace. Admin-only; the api refuses to remove the last admin. */
export function useRemoveMember() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (userId: string) =>
			unwrap(await api.DELETE('/v1/members/{userId}', { params: { path: { userId } } })),
		onError: toastApiError,
		onSuccess: () => {
			// Removing an active member frees a paid seat, so the billing summary goes
			// stale alongside the roster. Refetch both (mirrors useSetMemberStatus).
			queryClient.invalidateQueries({ queryKey: membersKey });
			queryClient.invalidateQueries({ queryKey: billingStatusKey });
		},
	});
}
